import { z } from "zod";

import {
  contentSlugSchema,
  isContentReservedEntrySlug,
} from "./contentCollection";

export const contentEntryCommandValueSchema = z.union([
  z.string(),
  z.boolean(),
  z.array(z.string()),
  z.null(),
]);

const contentEntryTargetSchema = z.object({
  siteId: z.uuid().describe("The site ID returned by list_sites."),
  collectionSlug: contentSlugSchema.describe(
    "The collection's URL slug and content directory name.",
  ),
  entrySlug: contentSlugSchema
    .refine((slug) => !isContentReservedEntrySlug(slug), {
      message: "This slug is reserved.",
    })
    .describe("The entry's URL slug and filename without .mdx."),
});

export const contentReadEntryInputSchema = contentEntryTargetSchema;

export const contentCreateEntryInputSchema = contentEntryTargetSchema.extend({
  status: z
    .enum(["draft", "published"])
    .default("draft")
    .describe("Create as a draft unless the entry is ready to publish."),
  values: z
    .record(z.string(), contentEntryCommandValueSchema)
    .optional()
    .default({})
    .describe(
      "Values keyed by collection field ID. Unknown or disabled fields are rejected.",
    ),
  body: z
    .string()
    .optional()
    .default("")
    .describe("Markdown body content, when the collection enables body."),
});

export const contentUpdateEntryInputSchema = contentEntryTargetSchema.extend({
  expectedRevision: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .describe("The revision returned by the latest content_read_entry call."),
  status: z
    .enum(["draft", "published"])
    .optional()
    .describe("Optional new publication status."),
  values: z
    .record(z.string(), contentEntryCommandValueSchema)
    .optional()
    .describe("Collection field values to set, keyed by field ID."),
  removeFields: z
    .array(z.string().min(1))
    .max(200)
    .optional()
    .describe("Collection field IDs whose values should be removed."),
  body: z
    .string()
    .optional()
    .describe("Replacement Markdown body, when the collection enables body."),
});

export const contentArchiveEntryInputSchema = contentEntryTargetSchema.extend({
  expectedRevision: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .describe("The revision returned by the latest content_read_entry call."),
});

export const contentEntryCommandSchema = z.discriminatedUnion("action", [
  contentReadEntryInputSchema.extend({ action: z.literal("read") }),
  contentCreateEntryInputSchema.extend({ action: z.literal("create") }),
  contentUpdateEntryInputSchema.extend({ action: z.literal("update") }),
  contentArchiveEntryInputSchema.extend({ action: z.literal("archive") }),
]);

export const contentEntryCommandRequestSchema = z.object({
  projectId: z.uuid(),
  userId: z.uuid(),
  command: contentEntryCommandSchema,
});

export const contentEntryCommandOutputSchema = z.object({
  path: z.string(),
  revision: z.string().regex(/^[a-f0-9]{64}$/),
  status: z.enum(["draft", "published", "archived"]),
  values: z.record(z.string(), contentEntryCommandValueSchema),
  body: z.string().nullable(),
});
